/**
 * Session-wide caches for SVG map assets (icons, furniture).
 *
 * Big maps render hundreds of shapes that reference the same handful of
 * SVG urls. Without these caches every shape instance fired its own
 * fetch (the per-component caches didn't dedupe *in-flight* requests, so
 * a map with 300 chairs issued 300 identical fetches on first paint) and
 * minted its own blob: URL + HTMLImageElement — over a thousand requests
 * on large maps. Everything here is keyed so identical assets resolve to
 * a single fetch and a single shared image element.
 */

/** Encode inline SVG markup as a data URI for use as an `<image href>`. */
export const toDataUri = (svg: string): string =>
  `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`;

const svgTextPromises = new Map<string, Promise<string>>();

/** Fetch an SVG's text, deduping concurrent and repeat requests. */
export const fetchSvgText = (url: string): Promise<string> => {
  let promise = svgTextPromises.get(url);
  if (!promise) {
    promise = fetch(url).then((response) => response.text());
    // Failed fetches are evicted so a later mount can retry.
    promise.catch(() => svgTextPromises.delete(url));
    svgTextPromises.set(url, promise);
  }
  return promise;
};

const imagePromises = new Map<string, Promise<HTMLImageElement>>();

const rasterizeSvgMarkup = (markup: string): Promise<HTMLImageElement> =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const blob = new Blob([markup], { type: 'image/svg+xml' });
    const blobUrl = URL.createObjectURL(blob);
    const img = new window.Image();
    img.onload = () => {
      URL.revokeObjectURL(blobUrl);
      resolve(img);
    };
    img.onerror = (event) => {
      URL.revokeObjectURL(blobUrl);
      reject(event);
    };
    img.src = blobUrl;
  });

/**
 * Load a url-sourced SVG as an image with `currentColor` substituted —
 * one shared HTMLImageElement per (url, color). Konva can draw the same
 * element in any number of nodes.
 */
export const loadColoredSvgUrlImage = (
  url: string,
  color: string
): Promise<HTMLImageElement> => {
  const key = `url:${url}::${color}`;
  let promise = imagePromises.get(key);
  if (!promise) {
    promise = fetchSvgText(url).then((svgText) =>
      rasterizeSvgMarkup(svgText.replace(/currentColor/g, color))
    );
    promise.catch(() => imagePromises.delete(key));
    imagePromises.set(key, promise);
  }
  return promise;
};

/**
 * Load inline SVG markup as an image with `currentColor` substituted —
 * one shared HTMLImageElement per (markup, color). Shapes stamped from
 * the same icon carry identical markup strings, so they all share one
 * rasterization instead of one per shape.
 */
export const loadColoredSvgMarkupImage = (
  markup: string,
  color: string
): Promise<HTMLImageElement> => {
  const key = `markup:${markup}::${color}`;
  let promise = imagePromises.get(key);
  if (!promise) {
    promise = rasterizeSvgMarkup(markup.replace(/currentColor/g, color));
    promise.catch(() => imagePromises.delete(key));
    imagePromises.set(key, promise);
  }
  return promise;
};
